import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  IconButton,
  Link,
  Modal,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import {
  COLOR_RED,
  COLOR_TOOLTIP_BACKGROUND,
  exchangeRates,
  GET_TOP_HOST_DETAILS_URL,
  RATE_LIMIT_ERROR_MESSAGE,
  TOP_HOST_TOOLTIP_MAP,
} from 'src/utils/const';
import {
  convertCurrency,
  formatCurrency,
  getUserRemainingTopHostUnlockCredits,
} from 'src/utils/functions';
import { LoadingCard } from 'src/App/Dashboards/Market/Components/Cards';
import { infoIconButton, infoIconStyle } from 'src/App/Dashboards/Market/styles/style';
import BasicTable from 'src/App/Dashboards/Market/Components/Tables/BasicTable';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LockSharpIcon from '@material-ui/icons/LockSharp';
import { logAmplitudeEvent } from 'src/utils/analytics';
import NoDataCard from 'src/App/Dashboards/Market/Components/Cards/NoDataCard';
import { VerifiedTag } from 'src/App/Dashboards/Market/Components/Tags';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import HostModal from './HostModal';
import { isHostLocked } from '../helper';
import TooltipNew from 'src/App/Dashboards/Market/Components/Tooltips/TooltipNew';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { callApi } from 'src/core/callApi';
import { Search } from 'src/interfaces/idashboard';
import { getTopHosts } from 'src/store/Dashboard/actions';
import { useDispatch } from 'react-redux';
import { InfoButton } from 'src/App/Dashboards/Market/Components/Buttons';
import RateLimitAlert from '../../../Components/Alerts/RateLimitAlert';

const Hosts = ({
  currency,
  topHosts,
  handleOnSampleClick,
  user,
  matches,
  dashboardLayoutView,
  userTailorMarketAccess,
  searchObj,
  isUserAllowed,
  cachedMarketData,
  warningTextComponent,
}: {
  currency: string;
  topHosts?: any;
  handleOnSampleClick: any;
  user: any;
  matches: boolean;
  dashboardLayoutView: 'map' | 'dashboard' | 'discovery' | 'compare' | 'advance' | 'searchListing';
  userTailorMarketAccess: boolean;
  searchObj: Search;
  isUserAllowed: boolean;
  cachedMarketData: any;
  warningTextComponent: any;
}) => {
  const [hoverTimeout, setHoverTimeout] = useState<NodeJS.Timeout | null>(null);
  const handleMouseEnter = () => {
    const timeout = setTimeout(() => {
      logAmplitudeEvent('top_hosts_card_interacted');
    }, 500);
    setHoverTimeout(timeout);
  };

  const handleMouseLeave = () => {
    hoverTimeout && clearTimeout(hoverTimeout);
  };

  const currencySymbol = exchangeRates[currency].symbol;

  const [listingHavingLicencePercentage, setListingHavingLicencePercentage] = useState<
    number | 'loading'
  >('loading');
  const isAccessDenied = !topHosts.isUserAllowedToHostData;

  const [openHostModal, setOpenHostModal] = useState<boolean>(false);
  const [host, setHost] = useState<any>('');
  const [modalWithAllHostOpen, setModalWithAllHostOpen] = useState<boolean>(false);

  const [warningModalConfig, setWarningModalConfig] = useState<{
    title: string;
    message: string;
    buttonText: string;
    onButtonClick: () => void;
    remainingCredits: number;
  } | null>(null);
  const [isWarningModalActionLoading, setIsWarningModalActionLoading] = useState<boolean>(false);
  const [warningModalError, setWarningModalError] = useState<string | null>(null);

  useEffect(() => {
    if (cachedMarketData.loading) return;

    const licensePercentage = cachedMarketData?.data?.supply_breakdown?.license_percentage;
    if (licensePercentage) {
      setListingHavingLicencePercentage(licensePercentage);
    } else {
      setListingHavingLicencePercentage(0);
    }
  }, [cachedMarketData]);

  const dispatch = useDispatch();

  const urlRef = useRef(new URL(window.location.href));

  useEffect(() => {
    if (urlRef.current.searchParams.get('top-airbnb-management-companies')) {
      setOpenHostModal(true);
      setModalWithAllHostOpen(true);
    }
  }, []);

  useEffect(() => {
    const url = new URL(urlRef.current);

    const updateUrl = (newUrl: string | URL) => {
      window.history.pushState({}, '', newUrl);
    };

    if (openHostModal) {
      host && !host.is_locked
        ? updateUrl(`/host/${host.id}`)
        : (() => {
            url.searchParams.set('top-airbnb-management-companies', 'true');
            updateUrl(url);
          })();
    } else {
      host
        ? updateUrl(url)
        : (() => {
            url.searchParams.delete('top-airbnb-management-companies');
            updateUrl(url);
          })();
    }
  }, [openHostModal, host]);

  let totalHostCount = topHosts.data?.length || 0;

  if (topHosts.isRateLimited) {
    return (
      <>
        <Grid item xs={12}>
          <Typography variant="body1" className="subHeadings">
            Top Airbnb Management Companies
          </Typography>
        </Grid>
        <RateLimitAlert />
      </>
    );
  }

  if (!topHosts.loading && !topHosts.data) {
    return (
      <NoDataCard
        height={150}
        borderRadius={8}
        customText="Top Airbnb management companies are available exclusively in verified markets. Use the search bar to find verified markets, marked with a blue checkmark."
      />
    );
  }  

  const handleOpenHostModal = (host: any, index: number) => {
    setOpenHostModal(true);
    setHost(host);
  };

  const getParsedTop5Hosts = (type: 'top10' | 'all') =>
    topHosts.data
      ? topHosts.data
          .map((host: any, index: number) => [
            <Link
              underline="none"
              onClick={() => handleOpenHostModal(host, index)}
              sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
            >
              {host.name} <OpenInNewRoundedIcon sx={{ ml: '4px', mr: '8px', fontSize: '12px' }} />{' '}
              {host.verification_status === 'verified' ? (
                <VerifiedTag size="small" tooltipTitle="Airbtics verified host." />
              ) : (
                <></>
              )}{' '}
            </Link>,
            formatCurrency(String(host.listings)),
            host.rating,
            currencySymbol + formatCurrency(String(convertCurrency(currency, host.revenue))),
            host.occupancy_rate + '%',
            currencySymbol + formatCurrency(String(convertCurrency(currency, host.adr))),
            ...(type === 'all' ? [host.most_common_bedroom, host.inventory_growth + '%'] : []),
          ])
          .slice(0, type === 'top10' ? 10 : topHosts.data.length)
      : [];

  const ViewAllHostButton: JSX.Element = (
    <Button
      onClick={() => {
        setHost('');
        setModalWithAllHostOpen(true);
        setOpenHostModal(true);
      }}
      variant="contained"
      color="primary"
      sx={{
        fontSize: 14,
        fontWeight: 600,
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      View All {totalHostCount > 0 ? totalHostCount : ''} Hosts{' '}
    </Button>
  );

  const getTableInfoIcon = (title: string, content: string) => {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
        {title}
        <TooltipNew title={title} content={content}>
          <IconButton>
            <InfoOutlinedIcon sx={{ fontSize: 12 }} />
          </IconButton>
        </TooltipNew>
      </div>
    );
  };

  const handleOnHostModalClose = () => {
    setHost('');
    setOpenHostModal(false);
    setModalWithAllHostOpen(false);
  };

  const handleUnlock = async () => {
    setIsWarningModalActionLoading(true);
    const response = await callApi(
      GET_TOP_HOST_DETAILS_URL,
      {
        queryParams: {
          marketID: String(searchObj.parentMarketID ? searchObj.parentMarketID : searchObj.id),
          returnUserAccess: true,
          unlockTopHosts: true,
          apiType: 'v3',
        },
      },
      true,
      false,
      '',
      'GET',
      true,
      undefined,
      true,
      true
    );

    if (response && response.status === 429) {
      setWarningModalError(RATE_LIMIT_ERROR_MESSAGE);
      setIsWarningModalActionLoading(false);
      return;
    }

    if (response.message) {
      setWarningModalConfig(null);
      setOpenHostModal(false);
      setModalWithAllHostOpen(false);
      setWarningModalError(null);
      dispatch(getTopHosts(String(searchObj.id)));
    } else {
      setWarningModalError(response.error);
    }
    setIsWarningModalActionLoading(false);
  };

  const handleUnlockTopHosts = () => {
    const remainingCredits = getUserRemainingTopHostUnlockCredits(user);
    setWarningModalConfig({
      title: 'Reveal Professional Hosts',
      message: `Revealing this data will deduct 1 View Top Airbnb Management Companies credit from your account. You have ${remainingCredits} remaining credits this month.`,
      buttonText: 'Proceed',
      onButtonClick: handleUnlock,
      remainingCredits: remainingCredits,
    });
  };

  const getHostTable = (
    type: 'top10' | 'all',
    renderingFrom: 'dashboard' | 'modal' = 'dashboard'
  ) => {
    return (
      <BasicTable
        header={[
          'Name',
          getTableInfoIcon('Listings', TOP_HOST_TOOLTIP_MAP.listings),
          getTableInfoIcon('Avg Rating', TOP_HOST_TOOLTIP_MAP.avg_rating),
          getTableInfoIcon('Avg Revenue', TOP_HOST_TOOLTIP_MAP.avg_revenue),
          getTableInfoIcon('Avg Occupancy Rate', TOP_HOST_TOOLTIP_MAP.avg_occupancy_rate),
          getTableInfoIcon('Avg Daily Rate', TOP_HOST_TOOLTIP_MAP.avg_daily_rate),
          ...(type === 'all'
            ? [
                getTableInfoIcon('Most Common Bedroom', TOP_HOST_TOOLTIP_MAP.most_common_bedroom),
                getTableInfoIcon('Inventory Growth', TOP_HOST_TOOLTIP_MAP.inventory_growth),
              ]
            : []),
        ]}
        rows={type === 'top10' ? getParsedTop5Hosts('top10') : getParsedTop5Hosts('all')}
        blurredColumns={isAccessDenied ? Array.from({ length: 6 }, (_, i) => i + 2) : []} // column 2 to 7
        skipRowsFromBlur={Array.from({ length: 5 }, (_, i) => i)} // Skip the first 5 rows
        onButtonClick={handleOnSampleClick}
        dashboardLayoutView={dashboardLayoutView}
        renderingFrom={renderingFrom}
        onModalClose={handleOnHostModalClose}
        handleUnlock={handleUnlockTopHosts}
      />
    );
  };

  return (
    <>
      {topHosts.loading && <LoadingCard height={150} type="bars" />}
      {!topHosts.loading && topHosts.data && (
        <Grid
          container
          direction="column"
          justifyContent="center"
          item
          xs={12}
          spacing={1}
          sx={{
            padding: '5px',
            boxShadow: '0 4px 10px #0003',
            borderRadius: 1,
            overflow: 'auto',
            paddingRight: '5px',
          }}
        >
          {warningTextComponent && warningTextComponent}
          <Grid item xs={12}>
            <Typography variant="body1" className="subHeadings">
              Top Airbnb Management Companies
              <InfoButton tooltipContent="Only Airbnb management companies and hosts with over 5 listings, sorted by listing count." />
            </Typography>
          </Grid>
          <Grid
            item
            xs={12}
            sx={{ overflow: 'auto' }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            {getHostTable('top10')}
          </Grid>
          <Grid
            container
            direction="row"
            justifyContent="space-between"
            item
            xs={12}
            sx={{ marginTop: '10px' }}
          >
            {ViewAllHostButton}
          </Grid>
          <Grid item xs={12}>
            <Tooltip
              placement="top-start"
              title={
                listingHavingLicencePercentage !== 'loading' &&
                listingHavingLicencePercentage <= 5 &&
                isUserAllowed
                  ? `${listingHavingLicencePercentage} % of the listings are allowed to operate short-term rentals with a local license.`
                  : ''
              }
            >
              <Typography sx={{ '&:hover': { cursor: 'pointer' }, fontSize: '12px' }}>
                {listingHavingLicencePercentage !== 'loading' &&
                listingHavingLicencePercentage <= 5 &&
                isUserAllowed ? (
                  'A local license may not be required to operate short-term rentals in this area.'
                ) : (
                  <>
                    {!isUserAllowed && (
                      <LockSharpIcon
                        style={{ fontSize: 15, color: 'inherit' }}
                        onClick={() => handleOnSampleClick('expert')}
                      />
                    )}
                    {isUserAllowed && listingHavingLicencePercentage !== 'loading' ? (
                      <CircularProgress size={15} />
                    ) : (
                      `${listingHavingLicencePercentage}% of the listings are allowed
                    to operate short-term rentals with a local license.`
                    )}
                  </>
                )}
              </Typography>
            </Tooltip>
          </Grid>
        </Grid>
      )}

      {openHostModal && (
        <HostModal
          open={openHostModal}
          onClose={handleOnHostModalClose}
          host={host}
          hosts={modalWithAllHostOpen ? topHosts.data : undefined}
          currency={currency}
          isMobileScreen={matches}
          setHost={setHost}
          handleOnSampleClick={() => {
            handleOnSampleClick('expert');
            setOpenHostModal(false);
            setModalWithAllHostOpen(false);
          }}
          user={user}
          getTableComponent={() => getHostTable('all', 'modal')}
          isAccessDenied={isAccessDenied}
          handleUnlockTopHosts={handleUnlockTopHosts}
          isLoading={topHosts.loading}
        />
      )}

      {warningModalConfig && (
        <WarningModal
          open={true}
          onClose={() => setWarningModalConfig(null)}
          warningModalConfig={warningModalConfig}
          isWarningModalActionLoading={isWarningModalActionLoading}
          warningModalError={warningModalError}
          showUpgradeToScale={
            user.subscriptionPlan === 'expert' && warningModalConfig.remainingCredits <= 0
          }
          handleUpgradeScaleClick={() => handleOnSampleClick('scale')}
        />
      )}
    </>
  );
};

const WarningModal = ({
  open,
  onClose,
  warningModalConfig,
  isWarningModalActionLoading,
  warningModalError,
  showUpgradeToScale,
  handleUpgradeScaleClick,
}: {
  open: boolean;
  onClose: () => void;
  warningModalConfig: {
    title: string;
    message: string;
    buttonText: string;
    onButtonClick: () => void;
    remainingCredits: number;
  } | null;
  isWarningModalActionLoading: boolean;
  warningModalError: string | null;
  showUpgradeToScale?: boolean;
  handleUpgradeScaleClick?: () => void;
}) => {
  if (!warningModalConfig) return <></>;

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 'clamp(300px, 90%, 500px)',
          backgroundColor: 'white',
          borderRadius: 4,
          boxShadow: 24,
          p: 4,
          outline: 'none',
        }}
      >
        <Grid container sx={{ gap: '20px' }}>
          <IconButton onClick={onClose} sx={{ position: 'absolute', top: '10px', right: '10px' }}>
            <CloseRoundedIcon sx={{ cursor: 'pointer', color: COLOR_RED, fontSize: 20 }} />
          </IconButton>

          <Grid item xs={12}>
            <Typography variant="h6">{warningModalConfig?.title}</Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body1">{warningModalConfig?.message}</Typography>
          </Grid>
          {warningModalError && (
            <Grid item xs={12}>
              <Alert severity="error">{warningModalError}</Alert>
            </Grid>
          )}
          <Grid container item xs={12} justifyContent="flex-end">
            <Button
              variant="contained"
              onClick={
                showUpgradeToScale && showUpgradeToScale
                  ? handleUpgradeScaleClick
                  : warningModalConfig?.onButtonClick
              }
              disabled={
                isWarningModalActionLoading ||
                (warningModalConfig.remainingCredits <= 0 && !showUpgradeToScale)
              }
            >
              {isWarningModalActionLoading ? (
                <CircularProgress size={20} />
              ) : showUpgradeToScale ? (
                'Upgrade to Scale Plan'
              ) : (
                warningModalConfig?.buttonText
              )}
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Modal>
  );
};

export default Hosts;
